import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, UserPlus, Users } from 'lucide-react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import AutoCollapseButton from 'src/components/common/AutoCollapseButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  useFlowStatuses,
  useRequirementLocations,
} from 'src/services/api/queries/opportunityQueries';
import {
  useCandidatesByStatusForTa,
  useCandidatesPipelineSummary,
} from 'src/services/api/queries/candidateQueries';
import { useToast } from 'src/hooks/use-toast';
import CandidateProfileModal from 'src/components/Modals/CandidateProfileModal';
import ExternalCandidatesModal from 'src/components/Modals/ExternalCandidatesModal';
import { getTabComponent } from './tabs';
import PipelineTabBar from './tabs/PipelineTabBar';
import PipelineSearchSortBar from './tabs/PipelineSearchSortBar';
import SubStatusFilter from './tabs/SubStatusFilter';
import PipelineOverviewHeader from './PipelineOverviewHeader';

const DEFAULT_PAGE_SIZE = 10;

// Same tonal count-badge treatment FilterButton uses for its active tab
// counts — reused here so a location's submitted count reads consistently
// with the rest of the page rather than as a plain text suffix.
const SubmittedCountBadge = ({ count }) => (
  <span className="shrink-0 text-[11px] font-semibold px-[7px] py-px rounded-[10px] leading-[1.5] bg-brand-bg-subtle text-primary">
    {count}
  </span>
);

SubmittedCountBadge.propTypes = {
  count: PropTypes.number.isRequired,
};

// TA's own view of the candidates they're pushing through an assignment —
// replaces RequirementPipeline for TA now that RequirementPipeline is CSE-only (TA Lead
// removal). Neither the tabs nor their tables/actions are hardcoded here:
// GET /assignments/:id/flow-statuses returns which flow this assignment
// uses (flow.state_machine_key), a separate top-level `tabs[]` array
// describing each pipeline stage (name, order_number, is_main_tab, and
// which underlying statuses it merges via filters.status), and a
// `statuses[]` array carrying each individual status's own manual
// (state-transition) actions (manual_actions: [{ key, label }]).
// state_machine_key + a tab's primary status together resolve to a
// registered component via tabs/tabRegistry.js (getTabComponent) — see
// tabs/ and actions/ for the actual per-flow, per-tab and per-flow,
// per-action components.
const AssignmentPipeline = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const assignmentId = searchParams.get('assignmentId');
  const opportunityId = searchParams.get('opportunityId');
  const { toast } = useToast();

  const [currentFilter, setCurrentFilter] = useState(null);
  // Which single underlying status to narrow the active tab down to, or
  // 'all' to fetch every status it merges together (the default) — see
  // SubStatusFilter.jsx. Reset whenever the active tab changes, since a
  // sub-status picked on one tab has no meaning on another.
  const [subStatusFilter, setSubStatusFilter] = useState('all');
  const [requirementLocationId, setRequirementLocationId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Debounced the same way UpcomingInterviews.jsx's own search box is —
  // plain useState + a 500ms setTimeout, not a shared hook. `searchQuery` is
  // what the input shows as the user types; `debouncedSearchQuery` is what
  // actually reaches the API, so a fast typist doesn't fire a request per
  // keystroke.
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // "View profile" (RowActionsCell) opens CandidateProfileModal in place
  // instead of navigating to a full page — one instance here, shared by
  // every tab, rather than each tab owning its own. `index` backs the
  // modal's Prev/Next pager (onNavigate below), looked up against this
  // page's own `candidates` array (declared further down).
  const [profileModal, setProfileModal] = useState({
    open: false,
    candidateId: null,
    index: -1,
  });
  // External Candidates (ExternalCandidatesModal) — candidates submitted
  // through a partner/direct-external source for this assignment, as
  // opposed to the TA's own pipeline above. Self-contained (fetches its own
  // list off assignmentId via useExternalCandidates), so this page only
  // owns whether it's open.
  const [externalCandidatesModalOpen, setExternalCandidatesModalOpen] =
    useState(false);

  const {
    data: flowStatusesResponse,
    isLoading: isFlowStatusesLoading,
    error: flowStatusesError,
  } = useFlowStatuses(assignmentId);

  const stateMachineKey =
    flowStatusesResponse?.data?.flow?.state_machine_key || 'default';
  const statuses = flowStatusesResponse?.data?.statuses || [];
  const tabs = flowStatusesResponse?.data?.tabs || [];

  // Tab identity — which statuses form a top-level tab, its display name,
  // its order, and which sub-statuses it merges — lives entirely in this
  // separate `tabs[]` array now, not on individual `statuses[]` entries.
  // `value` deliberately stays the tab's *primary* underlying status string
  // (filters.status[0]), not its cosmetic `name` — every other status-keyed
  // lookup on this page (action registry, tab registry, manualActionsByStatus,
  // tab count badges further down) is keyed by real status, so keeping
  // `value` a real status means none of that has to change; `label` is the
  // only place `name` is used, so the bar can show "Contact"/"Interview"/
  // "Shortlisted" etc. even though those differ from the underlying status
  // strings ("Contacted"/"In Progress"/"Completed").
  //
  // NOTE: this replaced an earlier shape where tab-worthiness lived on each
  // status as `statuses[].is_main_state` + `statuses[].filters` — confirmed
  // gone via a real GET .../flow-statuses response shipped by the backend:
  // `statuses[]` entries no longer carry `is_main_state`, `parent_state`, or
  // `filters` at all (replaced by `allowed_transitions`/`is_final`, and tab
  // identity moving to this `tabs[]` array). Reading `s.is_main_state` here
  // after that ship silently emptied the tab bar again — the same failure
  // shape as the earlier `status`→`state` field-name drift this page already
  // hit once. (Per-status fields that ARE unchanged: `state`, `manual_actions`.)
  const filters = useMemo(
    () =>
      tabs
        .filter((t) => t.is_main_tab)
        .sort((a, b) => a.order_number - b.order_number)
        .map((t) => ({ value: t.filters?.status?.[0], label: t.name }))
        .filter((f) => f.value),
    [tabs]
  );

  // The active tab's own entry in `statuses[]` — the source for its
  // manual_actions. (Which sub-statuses this tab merges now comes from
  // `tabs[]`/activeTabEntry below, not from here — see filters above.)
  const activeStatusEntry = useMemo(
    () => statuses.find((s) => s.state === currentFilter),
    [statuses, currentFilter]
  );

  // Every status's own manual_actions, keyed by its `state` string — the
  // source of truth for resolving actions per *individual candidate*
  // (RowActionsCell) rather than per tab. A merged tab's sub-statuses can
  // carry different manual_actions than the tab's own main state (e.g.
  // "Failed" itself allows move_to_shortlisted, but its merged siblings
  // "Not Interested"/"No Show"/"Non Responsive" allow nothing at all) — so
  // a candidate's row actions must come from *their own* real status, not
  // whichever tab they're currently grouped under.
  const manualActionsByStatus = useMemo(
    () =>
      Object.fromEntries(
        statuses.map((s) => [s.state, s.manual_actions || []])
      ),
    [statuses]
  );

  // A tab can merge more than one underlying candidate status — e.g. the
  // "Contact" tab also covers "Acknowledged", and "Closed" covers "Failed" +
  // "Not Interested" + "No Show" + "Non Responsive". The API tells us
  // exactly which statuses a tab covers via that tab's own `filters.status`
  // array (on `tabs[]` — see above). All of them are fetched together by
  // default — sending only the tab's own primary status string was a real
  // bug: acting on a candidate could move them to a merged sub-status, and
  // since nothing was ever fetched for that status, they'd silently vanish
  // from every tab. createEndpoint (endpoints.jsx) turns an array value into
  // repeated `status=` query params — the same
  // `?status=Contacted&status=Acknowledged` shape the API expects.
  // `subStatusFilter` (via SubStatusFilter.jsx) lets the user narrow that
  // merged set down to just one specific status.
  const activeTabEntry = useMemo(
    () => tabs.find((t) => t.filters?.status?.[0] === currentFilter),
    [tabs, currentFilter]
  );
  const subStatusOptions = activeTabEntry?.filters?.status || [];

  // The live manual_actions for the bulk action bar (ManualActionsBar) —
  // resolved from whichever single status is actually in effect: the picked
  // sub-status when the user has narrowed down to one via SubStatusFilter,
  // otherwise the tab's own main-state entry. Narrowing to "Acknowledged"
  // within "Contacted" must show Acknowledged's own actions (it has no
  // acknowledge_contact, sensibly), not Contacted's.
  const manualActions =
    (subStatusFilter !== 'all' && manualActionsByStatus[subStatusFilter]) ||
    activeStatusEntry?.manual_actions ||
    [];

  // Bulk-select (checkboxes + the floating manual-actions bar) is only safe
  // when every visible row is guaranteed to share one real status — either
  // this tab doesn't merge multiple statuses at all, or the user has
  // narrowed down to one specific one via SubStatusFilter. With "All
  // statuses" showing on a tab that merges several (e.g. Failed also covers
  // Not Interested/No Show/Non Responsive, each with different — often
  // empty — manual_actions), no single manual_actions array could be safely
  // bulk-applied across the whole selection, so selection itself is hidden
  // rather than offering actions that would fail for part of it.
  const selectable = subStatusOptions.length <= 1 || subStatusFilter !== 'all';

  const currentStatusFilters = useMemo(() => {
    const allStatuses = subStatusOptions.length
      ? subStatusOptions
      : [currentFilter].filter(Boolean);
    if (subStatusFilter !== 'all' && allStatuses.includes(subStatusFilter)) {
      return [subStatusFilter];
    }
    return allStatuses;
  }, [subStatusOptions, currentFilter, subStatusFilter]);

  // Which tab to land on once the flow loads: the URL's own `status` param
  // when it names a real tab on this flow (e.g. reloading the page, or
  // arriving via Preview.jsx's breadcrumb link back here, which already
  // carries `status`) — otherwise `tabs[]`'s first tab by order_number,
  // same fallback as before. Writing the resolved value back into the URL
  // (replace: true, so this never grows browser history) is what makes a
  // plain page reload land back on whichever tab was open, instead of
  // always resetting to the first one — previously `status` was accepted
  // in the URL but silently ignored by this page entirely.
  useEffect(() => {
    if (currentFilter || filters.length === 0) return;
    const statusParam = searchParams.get('status');
    const matched = filters.find((f) => f.value === statusParam);
    const initial = matched ? matched.value : filters[0].value;
    setCurrentFilter(initial);
    if (!matched) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('status', initial);
          return next;
        },
        { replace: true }
      );
    }
  }, [filters, currentFilter, searchParams, setSearchParams]);

  useEffect(() => {
    if (flowStatusesError) {
      toast({
        message: flowStatusesError.message || 'Failed to load flow statuses',
        variant: 'destructive',
      });
    }
  }, [flowStatusesError, toast]);

  const {
    data: locationsResponse,
    isLoading: isLocationsLoading,
    error: locationsError,
  } = useRequirementLocations(null, {
    assignmentId,
    enabled: !!assignmentId,
  });

  const requirementLocations =
    locationsResponse?.data?.assignment_locations || [];
  const assignmentInfo = locationsResponse?.data?.assignment_info || null;
  const selectedLocation = requirementLocations.find(
    (location) =>
      location.requirement_location_id.toString() === requirementLocationId
  );

  // Default to the first location once locations load.
  useEffect(() => {
    if (requirementLocationId || requirementLocations.length === 0) return;
    const firstId = requirementLocations[0]?.requirement_location_id;
    if (firstId) setRequirementLocationId(firstId.toString());
  }, [requirementLocationId, requirementLocations]);

  useEffect(() => {
    if (locationsError) {
      toast({
        message: locationsError.message || 'Failed to load locations',
        variant: 'destructive',
      });
    }
  }, [locationsError, toast]);

  const handleFilterChange = (value) => {
    setCurrentFilter(value);
    setSubStatusFilter('all');
    setPage(1);
    // Keeps the URL's `status` in sync with the clicked tab — see the
    // default-tab effect above for why this is what makes a reload persist
    // the tab instead of resetting to the first one.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('status', value);
        return next;
      },
      { replace: true }
    );
  };

  const handleSubStatusFilterChange = (value) => {
    setSubStatusFilter(value);
    setPage(1);
  };

  const handleLocationChange = (value) => {
    setRequirementLocationId(value);
    setPage(1);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(parseInt(value, 10));
    setPage(1);
  };

  // A fresh search result starts back on page 1 — same reasoning as every
  // other filter handler above, just reactive since the debounce means
  // there's no single handler moment to put this in.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery]);

  const {
    data: candidatesResponse,
    isLoading: isCandidatesLoading,
    isFetching: isCandidatesFetching,
    error: candidatesError,
    refetch: refetchCandidates,
  } = useCandidatesByStatusForTa({
    taLeadAssignmentId: assignmentId,
    status: currentStatusFilters,
    page,
    pageSize,
    requirementLocationId,
    search: debouncedSearchQuery || undefined,
    enabled: !!assignmentId && !!requirementLocationId && !!currentFilter,
  });

  const { data: pipelineSummaryResponse } = useCandidatesPipelineSummary({
    assignmentId,
    requirementLocationId,
  });
  const pipelineSummary = pipelineSummaryResponse?.data;

  useEffect(() => {
    if (candidatesError) {
      toast({
        message: candidatesError.message || 'Failed to fetch candidates',
        variant: 'destructive',
      });
    }
  }, [candidatesError, toast]);

  const candidates = candidatesResponse?.data?.items || [];

  // CandidateProfileModal's header falls back to `candidates[currentIndex]
  // .name` while the real profile is still loading — this list's rows carry
  // first_name/last_name (see candidateShape), not a combined `name`, so
  // that fallback would render blank for a beat without this.
  const profileModalCandidates = useMemo(
    () =>
      candidates.map((c) => ({
        ...c,
        name: [c.first_name, c.last_name].filter(Boolean).join(' '),
      })),
    [candidates]
  );

  const openProfileModal = (candidateId) => {
    const index = candidates.findIndex((c) => c.candidate_id === candidateId);
    setProfileModal({ open: true, candidateId, index });
  };

  const handleProfileModalNavigate = (newIndex) => {
    setProfileModal({
      open: true,
      candidateId: candidates[newIndex]?.candidate_id ?? null,
      index: newIndex,
    });
  };

  // The row the modal is currently showing — its own `status` (each row can
  // have a different real status than the tab it's grouped under, see the
  // manualActionsByStatus comment above) feeds the modal's "Full page" link
  // below, alongside this page's own assignmentId/requirementLocationId —
  // the same context RowActionsCell's old same-tab navigate() used to carry
  // into the URL (see RowActionsCell.jsx and CandidateProfileModal.jsx's
  // fullPageQuery prop comment for why the `state.from` breadcrumb flag
  // itself can't come along for the ride here).
  const profileModalRow = candidates.find(
    (c) => c.candidate_id === profileModal.candidateId
  );
  const profileModalFullPageQuery = {
    ...(profileModalRow?.status ? { status: profileModalRow.status } : {}),
    ...(assignmentId ? { assignmentId } : {}),
    ...(requirementLocationId ? { requirementLocationId } : {}),
  };

  const totalItems =
    candidatesResponse?.data?.total_candidates ??
    candidatesResponse?.data?.pagination?.total ??
    0;
  const totalPages = candidatesResponse?.data?.pagination?.total_pages ?? 1;
  // Per-tab counts (the "17" / "5" / "2" badges in the tab bar) — mirrors
  // the old RequirementPipeline.jsx's `stats` handling: `stats` is keyed by
  // requirement_location_id first (not by status directly), with the
  // per-status counts nested underneath that.
  //
  // `stats` itself is a static, complete breakdown of every status —
  // confirmed live it comes back identical no matter which `status`
  // param(s) were actually in the request. Two things follow from that,
  // both bugs when this just read `stats` straight through:
  //  1. A merged tab's badge (e.g. "Closed" = Failed + Not Interested +
  //     No Show + Non Responsive) needs to SUM every status it covers
  //     (tab.filters.status), not just read its primary status alone —
  //     that undercounted every merged tab down to just its first status.
  //  2. `stats` can never reflect the SubStatusFilter dropdown (narrowing
  //     to one specific sub-status re-fetches with a different `status`
  //     param, but `stats` doesn't move) — so the *active* tab's own badge
  //     is overridden with `totalItems` instead, since that IS scoped to
  //     whatever status(es) were actually just requested (it already
  //     drives the "showing 1 to N of N" pagination footer), and tracks
  //     the dropdown live the way `stats` structurally cannot.
  const rawTabCounts = useMemo(() => {
    const statusStats =
      (requirementLocationId
        ? candidatesResponse?.data?.stats?.[requirementLocationId]
        : candidatesResponse?.data?.stats) || null;
    if (!statusStats) return null;
    const counts = {};
    tabs.forEach((tab) => {
      const tabStatuses = tab.filters?.status || [];
      const primary = tabStatuses[0];
      if (!primary) return;
      counts[primary] = tabStatuses.reduce(
        (sum, s) => sum + (statusStats[s] || 0),
        0
      );
    });
    if (currentFilter) counts[currentFilter] = totalItems;
    return counts;
  }, [
    candidatesResponse,
    requirementLocationId,
    tabs,
    currentFilter,
    totalItems,
  ]);

  // Switching tabs changes the candidates query key, so React Query has no
  // cached data for the new one (gcTime: 0) — `candidatesResponse` is
  // briefly undefined and `rawTabCounts` goes null, blanking every badge
  // until the refetch lands. `stats` comes back byte-identical for every
  // status param though (see the comment above), so the badges never
  // actually need to change on a tab switch — hold the last computed set on
  // screen through the gap so they don't flicker out and snap back.
  const lastTabCountsRef = useRef(null);
  if (rawTabCounts) lastTabCountsRef.current = rawTabCounts;
  const tabCounts = rawTabCounts ?? lastTabCountsRef.current;

  // "Today at a glance" tiles (PipelineOverviewHeader) — backed by
  // GET .../dashboard/assignments/candidates-pipeline/summary
  // (useCandidatesPipelineSummary above), scoped to this assignment +
  // requirement location.
  const needsYouTodayCount = pipelineSummary?.needs_you_today ?? 0;
  const waitingOnCandidateCount = pipelineSummary?.waiting_on_candidate ?? 0;
  const interviewsThisWeekCount = pipelineSummary?.interviews_this_week ?? 0;
  const autoClosingTonightCount = pipelineSummary?.auto_closing_tonight ?? 0;

  const TabComponent = getTabComponent(stateMachineKey, currentFilter);

  const renderFilters = () => {
    if (isFlowStatusesLoading) {
      return (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      );
    }
    if (filters.length > 0) {
      return (
        <PipelineTabBar
          filters={filters}
          activeFilter={currentFilter}
          onFilterChange={handleFilterChange}
          counts={tabCounts}
          onRefresh={() => refetchCandidates()}
          isRefreshing={isCandidatesFetching}
        />
      );
    }
    return (
      <p className="text-[13px] text-muted-foreground">
        {assignmentId
          ? 'No pipeline stages found for this assignment.'
          : 'Missing assignmentId — open this page from an assignment.'}
      </p>
    );
  };

  // Mirrors the old RequirementPipeline.jsx's breadcrumb pattern: Home → the
  // opportunity (if we have one to link to) → this page.
  //
  // assignmentId/requirementLocationId are carried along the same way
  // handleSubmitCandidates (below) already does — OpportunityDetails.jsx
  // reads both PURELY from its own URL search params, no fallback to
  // anything else. Without them, landing there from this breadcrumb lost
  // that context entirely: its own "Submit Candidates" button's click
  // target silently drops &assignmentId=..., and its Assign Partner/
  // Publish flows (which explicitly guard on assignmentId being present)
  // stop working — not a rendering bug, a lost-context one.
  const oppDetailParams = new URLSearchParams();
  if (assignmentId) oppDetailParams.set('assignmentId', assignmentId);
  if (requirementLocationId)
    oppDetailParams.set('requirementLocationId', requirementLocationId);
  const oppDetailQuery = oppDetailParams.toString();
  const oppDetailHref = opportunityId
    ? `/opportunity/${opportunityId}${oppDetailQuery ? `?${oppDetailQuery}` : ''}`
    : null;
  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    ...(oppDetailHref
      ? [{ label: assignmentInfo?.title || 'Opportunity', href: oppDetailHref }]
      : []),
    { label: 'Screening Pipeline' },
  ];

  const locationSelect = requirementLocations.length > 0 && (
    <Select
      value={requirementLocationId || undefined}
      onValueChange={handleLocationChange}
    >
      <SelectTrigger className="w-64 h-9 text-sm rounded-lg bg-card text-foreground-strong border-input">
        <SelectValue placeholder="Select location">
          {selectedLocation && (
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <span className="truncate">
                {selectedLocation.location_name} —{' '}
                {selectedLocation.employment_type}
              </span>
              {selectedLocation.candidates_submitted > 0 && (
                <SubmittedCountBadge
                  count={selectedLocation.candidates_submitted}
                />
              )}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {requirementLocations.map((location) => (
          <SelectItem
            key={location.requirement_location_id}
            value={location.requirement_location_id.toString()}
          >
            <span className="inline-flex items-center gap-1.5">
              <span>
                {location.location_name} — {location.employment_type}
              </span>
              {location.candidates_submitted > 0 && (
                <SubmittedCountBadge count={location.candidates_submitted} />
              )}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // Mirrors the "View Submitted" button on SubmitCandidate.jsx (which
  // navigates the other way, into this page) — lets a TA jump back to
  // submit more candidates against the same assignment/location without
  // retracing the opportunity → Submit Candidates path.
  const handleSubmitCandidates = () => {
    const p = new URLSearchParams({ opportunityId: opportunityId || '' });
    if (assignmentId) p.set('assignmentId', assignmentId);
    if (requirementLocationId)
      p.set('requirementLocationId', requirementLocationId);
    navigate(`/candidates/submit?${p.toString()}`);
  };

  const headerActions = (
    <div className="flex items-center gap-2">
      {locationSelect}
      <AutoCollapseButton
        icon={Users}
        label="External Candidates"
        onClick={() => setExternalCandidatesModalOpen(true)}
        disabled={!assignmentId}
        className="h-9 rounded-lg px-3 text-[13px]"
      />
      <AutoCollapseButton
        icon={UserPlus}
        label="Submit Candidates"
        onClick={handleSubmitCandidates}
        disabled={!opportunityId}
        className="h-9 rounded-lg px-3 text-[13px]"
      />
    </div>
  );

  return (
    <div>
      <PageHeader breadcrumbs={breadcrumbItems} actions={headerActions} />
      <PipelineOverviewHeader
        assignmentInfo={assignmentInfo}
        assignmentLocations={requirementLocations}
        flowName={flowStatusesResponse?.data?.flow?.name}
        needsYouToday={needsYouTodayCount}
        waitingOnCandidate={waitingOnCandidateCount}
        interviewsThisWeek={interviewsThisWeekCount}
        autoClosingTonight={autoClosingTonightCount}
      />
      <PageContainer>
        {renderFilters()}
        {filters.length > 0 && (
          <>
            <PipelineSearchSortBar
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
            >
              {subStatusOptions.length > 1 && (
                <SubStatusFilter
                  statuses={subStatusOptions}
                  value={subStatusFilter}
                  onChange={handleSubStatusFilterChange}
                />
              )}
            </PipelineSearchSortBar>
            {TabComponent ? (
              <TabComponent
                candidates={candidates}
                // Includes isCandidatesFetching (not just isCandidatesLoading)
                // so the skeleton rows below also show for a manual refetch
                // (the tab bar's own Refresh button, or any action's
                // onActionSuccess) — React Query's `isLoading` alone only
                // covers the very first fetch, since gcTime: 0 makes a tab
                // switch look like one too but a same-tab refetch with data
                // already on screen wouldn't otherwise show anything at all.
                isLoading={
                  isCandidatesLoading ||
                  isCandidatesFetching ||
                  isLocationsLoading
                }
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                totalItems={totalItems}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
                opportunityId={opportunityId}
                assignmentId={assignmentId}
                requirementLocationId={requirementLocationId}
                stateMachineKey={stateMachineKey}
                manualActions={manualActions}
                manualActionsByStatus={manualActionsByStatus}
                selectable={selectable}
                onActionSuccess={refetchCandidates}
                onViewProfile={openProfileModal}
              />
            ) : (
              <p className="text-[13px] text-muted-foreground">
                No view configured for this stage yet.
              </p>
            )}
          </>
        )}
      </PageContainer>
      <CandidateProfileModal
        isOpen={profileModal.open}
        onClose={() => setProfileModal((prev) => ({ ...prev, open: false }))}
        candidateId={profileModal.candidateId}
        opportunityId={opportunityId}
        candidates={profileModalCandidates}
        currentIndex={profileModal.index}
        onNavigate={handleProfileModalNavigate}
        fullPageQuery={profileModalFullPageQuery}
      />
      <ExternalCandidatesModal
        isOpen={externalCandidatesModalOpen}
        onClose={() => setExternalCandidatesModalOpen(false)}
        assignmentId={assignmentId}
      />
    </div>
  );
};

export default AssignmentPipeline;
